// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.

import type { TraceParams, TraceResult } from './types';
import { PRESETS, matchPreset } from './presets';
import { formatBytes } from './imageutil';
import { GLOSSARY } from './glossary';
import { mountEventTrail, eventCount } from './eventlog';

export interface UIHandlers {
  onFile: (file: File) => void;
  onSample: () => void;
  onChange: () => void;
  onReset: () => void;
  onDownload: () => void;
  onCopySvg: () => void;
  onCopyPng: () => void;
  onShare: () => void;
}

export interface UI {
  getParams: () => TraceParams;
  setParams: (p: TraceParams) => void;
  showStage: (stage: 'empty' | 'working') => void;
  setBusy: (on: boolean, phase?: string, pct?: number) => void;
  showResult: (res: TraceResult, svgUrl: string, rasterUrl: string) => void;
  setError: (msg: string) => void;
  clearError: () => void;
  toast: (msg: string) => void;
  setShareVisible: (on: boolean) => void;
  setFileName: (name: string) => void;
  closeOverlays: () => boolean;
}

const g = (t: string) =>
  `<span class="glossary-link" data-term="${t}" tabindex="0" role="button">${t}</span>`;

export function createUI(root: HTMLElement, h: UIHandlers): UI {
  root.innerHTML = SHELL;

  // Query refs -------------------------------------------------------------
  const $ = <T extends HTMLElement = HTMLElement>(sel: string) => root.querySelector(sel) as T;
  const stageEmpty = $('#stage-empty');
  const stageWorking = $('#stage-working');
  const dropzone = $('#dropzone');
  const fileInput = $<HTMLInputElement>('#file-input');
  const errorBar = $('#error-bar');
  const errorMsg = $('#error-msg');
  const busy = $('#busy');
  const busyLabel = $('#busy-label');
  const busyBar = $('#busy-bar');
  const compare = $('#compare');
  const rasterImg = $<HTMLImageElement>('#raster-img');
  const svgImg = $<HTMLImageElement>('#svg-img');
  const slider = $<HTMLInputElement>('#slider');
  const statBar = $('#stat-bar');
  const fileNameEl = $('#file-name');
  const presetRow = $('#preset-row');
  const controls = $('#controls');
  const shareBtn = $<HTMLButtonElement>('#act-share');
  const toastEl = $('#toast');
  const modalHost = $('#modal-host');
  const drawer = $('#drawer');
  const drawerTrail = $('#drawer-trail');

  // ── Drop zone + file input ────────────────────────────────────────────
  const openPicker = () => fileInput.click();
  dropzone.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).closest('#sample-btn')) return;
    openPicker();
  });
  dropzone.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openPicker();
    }
  });
  fileInput.addEventListener('change', () => {
    const f = fileInput.files?.[0];
    if (f) h.onFile(f);
    fileInput.value = '';
  });
  $('#sample-btn').addEventListener('click', (e) => {
    e.stopPropagation();
    h.onSample();
  });
  $('#new-image').addEventListener('click', () => h.onReset());
  $('#error-dismiss').addEventListener('click', () => errorBar.classList.remove('show'));

  // Drag & drop (whole document, with a visual affordance on the zone)
  let dragDepth = 0;
  const setDrag = (on: boolean) => dropzone.classList.toggle('dragover', on);
  window.addEventListener('dragenter', (e) => {
    e.preventDefault();
    dragDepth++;
    setDrag(true);
  });
  window.addEventListener('dragover', (e) => e.preventDefault());
  window.addEventListener('dragleave', (e) => {
    e.preventDefault();
    dragDepth = Math.max(0, dragDepth - 1);
    if (dragDepth === 0) setDrag(false);
  });
  window.addEventListener('drop', (e) => {
    e.preventDefault();
    dragDepth = 0;
    setDrag(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) h.onFile(f);
  });

  // Paste an image from the clipboard
  window.addEventListener('paste', (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const it of items) {
      if (it.type.startsWith('image/')) {
        const f = it.getAsFile();
        if (f) {
          h.onFile(f);
          break;
        }
      }
    }
  });

  // ── Preset chips ──────────────────────────────────────────────────────
  const presetButtons = new Map<string, HTMLButtonElement>();
  for (const group of ['Colour', 'Black & white'] as const) {
    const wrap = document.createElement('div');
    wrap.className = 'preset-group';
    const lbl = document.createElement('span');
    lbl.className = 'preset-group-label';
    lbl.textContent = group;
    wrap.appendChild(lbl);
    for (const p of PRESETS.filter((x) => x.group === group)) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'preset-chip';
      b.textContent = p.label;
      b.title = p.hint;
      b.addEventListener('click', () => {
        setParams({ ...p.params });
        h.onChange();
      });
      presetButtons.set(p.id, b);
      wrap.appendChild(b);
    }
    presetRow.appendChild(wrap);
  }

  // ── Controls (advanced) ───────────────────────────────────────────────
  const state: TraceParams = { ...PRESETS[0].params };

  const modeColor = $<HTMLInputElement>('#mode-color');
  const modeBw = $<HTMLInputElement>('#mode-bw');
  const levels = $<HTMLInputElement>('#c-levels');
  const speckle = $<HTMLInputElement>('#c-speckle');
  const corner = $<HTMLInputElement>('#c-corner');
  const optimize = $<HTMLInputElement>('#c-optimize');
  const maxdim = $<HTMLInputElement>('#c-maxdim');
  const levelsRow = $('#row-levels');

  const readControls = (): TraceParams => ({
    color: modeColor.checked,
    levels: parseInt(levels.value, 10),
    speckle: parseInt(speckle.value, 10),
    corner: parseFloat(corner.value),
    optimize: parseFloat(optimize.value),
    maxDim: parseInt(maxdim.value, 10),
  });

  const syncOutputs = () => {
    $('#v-levels').textContent = String(state.levels);
    $('#v-speckle').textContent = `${state.speckle} px`;
    $('#v-corner').textContent = state.corner.toFixed(2);
    $('#v-optimize').textContent = state.optimize.toFixed(2);
    $('#v-maxdim').textContent = state.maxDim === 0 ? 'Source' : `${state.maxDim} px`;
    levelsRow.style.display = state.color ? '' : 'none';
    // Reflect which preset (if any) matches.
    const id = matchPreset(state);
    for (const [pid, btn] of presetButtons) btn.classList.toggle('active', pid === id);
    $('#custom-tag').classList.toggle('show', id === null);
  };

  const writeControls = () => {
    modeColor.checked = state.color;
    modeBw.checked = !state.color;
    levels.value = String(state.levels);
    speckle.value = String(state.speckle);
    corner.value = String(state.corner);
    optimize.value = String(state.optimize);
    maxdim.value = String(state.maxDim);
    syncOutputs();
  };

  const onControlInput = () => {
    Object.assign(state, readControls());
    syncOutputs();
    h.onChange();
  };
  for (const el of [modeColor, modeBw, levels, speckle, corner, optimize, maxdim]) {
    el.addEventListener('input', onControlInput);
    el.addEventListener('change', onControlInput);
  }

  // ── Action buttons ────────────────────────────────────────────────────
  $('#act-download').addEventListener('click', () => h.onDownload());
  $('#act-copysvg').addEventListener('click', () => h.onCopySvg());
  $('#act-copypng').addEventListener('click', () => h.onCopyPng());
  shareBtn.addEventListener('click', () => h.onShare());

  // ── Compare slider ────────────────────────────────────────────────────
  const applySlider = () => {
    const v = Number(slider.value);
    // Reveal the raster from the left up to v%.
    rasterImg.style.clipPath = `inset(0 ${100 - v}% 0 0)`;
    compare.style.setProperty('--split', `${v}%`);
  };
  slider.addEventListener('input', applySlider);

  // ── Modals ────────────────────────────────────────────────────────────
  const openModal = (title: string, bodyHtml: string) => {
    modalHost.innerHTML = `
      <div class="modal-backdrop" data-close></div>
      <div class="modal" role="dialog" aria-modal="true" aria-label="${title}">
        <div class="modal-head">
          <h2>${title}</h2>
          <button class="icon-btn" data-close aria-label="Close">${X_ICON}</button>
        </div>
        <div class="modal-body">${bodyHtml}</div>
      </div>`;
    modalHost.hidden = false;
    modalHost.querySelectorAll('[data-close]').forEach((el) =>
      el.addEventListener('click', closeModal),
    );
    wireGlossary(modalHost);
    (modalHost.querySelector('.icon-btn') as HTMLElement)?.focus();
  };
  const closeModal = () => {
    modalHost.hidden = true;
    modalHost.innerHTML = '';
  };
  $('#nav-how').addEventListener('click', () => openModal('How it works', HOW_HTML));
  $('#nav-privacy').addEventListener('click', () => openModal('Privacy', PRIVACY_HTML));
  $('#nav-about').addEventListener('click', () => openModal('About Vectorwell', ABOUT_HTML));
  $('#trust-banner').addEventListener('click', () => openModal('Privacy', PRIVACY_HTML));

  // ── Event drawer ──────────────────────────────────────────────────────
  const disposeTrail = mountEventTrail(drawerTrail);
  const setDrawer = (open: boolean) => {
    drawer.classList.toggle('open', open);
    drawer.setAttribute('aria-hidden', String(!open));
    $('#drawer-toggle').setAttribute('aria-expanded', String(open));
  };
  $('#drawer-toggle').addEventListener('click', () => setDrawer(!drawer.classList.contains('open')));
  $('#drawer-close').addEventListener('click', () => setDrawer(false));

  // ── Global keyboard ───────────────────────────────────────────────────
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if (closeOverlays()) e.preventDefault();
    }
  });

  const closeOverlays = (): boolean => {
    if (!modalHost.hidden) {
      closeModal();
      return true;
    }
    if (drawer.classList.contains('open')) {
      setDrawer(false);
      return true;
    }
    const tip = document.getElementById('glossary-tip');
    if (tip && tip.classList.contains('show')) {
      tip.classList.remove('show');
      return true;
    }
    return false;
  };

  // Glossary tooltip
  wireGlossary(root);

  // ── Public API implementation ─────────────────────────────────────────
  function setParams(p: TraceParams) {
    Object.assign(state, p);
    writeControls();
  }

  function showResult(res: TraceResult, svgUrl: string, rasterUrl: string) {
    rasterImg.src = rasterUrl;
    svgImg.src = svgUrl;
    slider.value = '50';
    applySlider();
    statBar.innerHTML = `
      <span class="stat"><b>${res.width}×${res.height}</b><i>viewBox</i></span>
      <span class="stat"><b>${res.paths.toLocaleString()}</b><i>${g('path')}s</i></span>
      <span class="stat"><b>${res.colors}</b><i>colour${res.colors === 1 ? '' : 's'}</i></span>
      <span class="stat"><b>${formatBytes(res.bytes)}</b><i>SVG size</i></span>
      <span class="stat"><b>${res.ms} ms</b><i>traced @ ${res.tracedWidth}×${res.tracedHeight}</i></span>`;
    wireGlossary(statBar);
  }

  function toast(msg: string) {
    toastEl.textContent = msg;
    toastEl.classList.add('show');
    window.clearTimeout((toast as unknown as { t?: number }).t);
    (toast as unknown as { t?: number }).t = window.setTimeout(
      () => toastEl.classList.remove('show'),
      2200,
    );
  }

  return {
    getParams: () => ({ ...state }),
    setParams,
    showStage(stage) {
      stageEmpty.hidden = stage !== 'empty';
      stageWorking.hidden = stage !== 'working';
    },
    setBusy(on, phase, pct) {
      busy.classList.toggle('show', on);
      busy.setAttribute('aria-hidden', String(!on));
      if (phase) busyLabel.textContent = phase;
      const indeterminate = pct == null;
      busyBar.classList.toggle('indeterminate', indeterminate);
      busyBar.style.setProperty('--pct', `${pct ?? 0}%`);
    },
    showResult,
    setError(msg) {
      errorMsg.textContent = msg;
      errorBar.classList.add('show');
    },
    clearError() {
      errorBar.classList.remove('show');
    },
    toast,
    setShareVisible(on) {
      shareBtn.hidden = !on;
    },
    setFileName(name) {
      fileNameEl.textContent = name;
    },
    closeOverlays,
  };

  // Keep controls referenced (silence unused in strict builds via usage above)
  void controls;
  void disposeTrail;
  void eventCount;
}

// ── Glossary tooltip wiring (shared) ─────────────────────────────────────
function wireGlossary(scope: HTMLElement): void {
  let tip = document.getElementById('glossary-tip');
  if (!tip) {
    tip = document.createElement('div');
    tip.id = 'glossary-tip';
    document.body.appendChild(tip);
    document.addEventListener('click', (e) => {
      if (!(e.target as HTMLElement).closest('.glossary-link') && tip)
        tip.classList.remove('show');
    });
  }
  const show = (el: HTMLElement) => {
    const term = (el.dataset.term || '').toLowerCase();
    const def = GLOSSARY[term];
    if (!def || !tip) return;
    tip.textContent = def;
    const r = el.getBoundingClientRect();
    tip.classList.add('show');
    const tw = Math.min(300, window.innerWidth - 24);
    tip.style.width = `${tw}px`;
    let left = r.left + r.width / 2 - tw / 2;
    left = Math.max(12, Math.min(left, window.innerWidth - tw - 12));
    tip.style.left = `${left}px`;
    tip.style.top = `${r.bottom + 8}px`;
  };
  scope.querySelectorAll<HTMLElement>('.glossary-link').forEach((el) => {
    if (el.dataset.wired) return;
    el.dataset.wired = '1';
    el.addEventListener('click', (e) => {
      e.stopPropagation();
      show(el);
    });
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        show(el);
      }
    });
  });
}

// ── Static markup ────────────────────────────────────────────────────────
const X_ICON = `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>`;

const LOGO = `<svg viewBox="0 0 64 64" width="26" height="26" aria-hidden="true"><rect width="64" height="64" rx="14" fill="var(--accent-primary)"/><path d="M14 44 C 22 18, 42 18, 50 44" fill="none" stroke="#fff" stroke-width="4" stroke-linecap="round"/><g fill="#fff"><rect x="9" y="39" width="10" height="10" rx="2.5"/><rect x="45" y="39" width="10" height="10" rx="2.5"/><circle cx="32" cy="18" r="4.4"/></g><g fill="none" stroke="#fff" stroke-width="2.2" opacity="0.85"><line x1="19" y1="44" x2="32" y2="18"/><line x1="45" y1="44" x2="32" y2="18"/></g></svg>`;

const SHELL = /* html */ `
<header class="topbar">
  <div class="brand">${LOGO}<span>Vectorwell</span></div>
  <nav class="nav">
    <button id="nav-how" class="nav-btn" type="button">How it works</button>
    <button id="nav-privacy" class="nav-btn" type="button">Privacy</button>
    <button id="nav-about" class="nav-btn" type="button">About</button>
    <button id="drawer-toggle" class="nav-btn ghost" type="button" aria-expanded="false" aria-controls="drawer">
      Log <span class="badge"><b id="ev-count">0</b></span>
    </button>
  </nav>
</header>

<main class="main-content">
  <button id="trust-banner" class="trust-banner" type="button">
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
    Runs entirely in your browser. Your image never leaves your device.
  </button>

  <div id="error-bar" class="error-bar">
    <span id="error-msg"></span>
    <button id="error-dismiss" class="icon-btn" type="button" aria-label="Dismiss">${X_ICON}</button>
  </div>

  <!-- EMPTY STAGE -->
  <section id="stage-empty" class="stage-empty">
    <div id="dropzone" class="dropzone" tabindex="0" role="button" aria-label="Choose an image to vectorize">
      <div class="dz-icon"><svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg></div>
      <div class="dz-title">Drop an image, paste, or click to choose</div>
      <div class="dz-sub">PNG · JPG · WEBP · GIF · BMP — turned into a scalable ${g('svg')}. Nothing is uploaded.</div>
      <button id="sample-btn" class="pill" type="button">Try a sample</button>
    </div>
  </section>

  <!-- WORKING STAGE -->
  <section id="stage-working" class="stage-working" hidden>
    <div class="work-grid">
      <div class="preview-col">
        <div class="preview-head">
          <span id="file-name" class="file-name">image</span>
          <button id="new-image" class="text-btn" type="button">Choose another</button>
        </div>
        <div id="compare" class="compare checker">
          <img id="svg-img" class="layer svg-layer" alt="Traced SVG result" />
          <img id="raster-img" class="layer raster-layer" alt="Original raster image" />
          <div class="split-line"></div>
          <span class="cmp-label left">Original</span>
          <span class="cmp-label right">SVG</span>
          <input id="slider" class="cmp-slider" type="range" min="0" max="100" value="50" aria-label="Compare original and SVG" />
          <div id="busy" class="busy" aria-hidden="true">
            <div class="busy-inner">
              <div id="busy-bar" class="busy-bar"><span></span></div>
              <div id="busy-label" class="busy-label">Tracing…</div>
            </div>
          </div>
        </div>
        <div id="stat-bar" class="stat-bar"></div>
      </div>

      <aside class="controls-col">
        <div class="panel">
          <div class="panel-title">Preset <span id="custom-tag" class="custom-tag">Custom</span></div>
          <div id="preset-row" class="preset-row"></div>
        </div>

        <div id="controls" class="panel">
          <div class="panel-title">Adjust</div>

          <div class="ctl">
            <div class="ctl-label">Mode</div>
            <div class="seg">
              <label class="seg-opt"><input type="radio" name="mode" id="mode-color" checked><span>Colour</span></label>
              <label class="seg-opt"><input type="radio" name="mode" id="mode-bw"><span>Black &amp; white</span></label>
            </div>
          </div>

          <div class="ctl" id="row-levels">
            <div class="ctl-label"><span>${g('posterize')} colours</span><output id="v-levels">5</output></div>
            <input id="c-levels" type="range" min="2" max="16" step="1" value="5">
          </div>

          <div class="ctl">
            <div class="ctl-label"><span>${g('speckle')} removal</span><output id="v-speckle">8 px</output></div>
            <input id="c-speckle" type="range" min="0" max="30" step="1" value="8">
          </div>

          <div class="ctl">
            <div class="ctl-label"><span>Corner threshold</span><output id="v-corner">1.00</output></div>
            <input id="c-corner" type="range" min="0" max="1.334" step="0.033" value="1">
          </div>

          <div class="ctl">
            <div class="ctl-label"><span>Curve smoothing</span><output id="v-optimize">0.20</output></div>
            <input id="c-optimize" type="range" min="0.05" max="1" step="0.05" value="0.2">
          </div>

          <div class="ctl">
            <div class="ctl-label"><span>${g('trace resolution')}</span><output id="v-maxdim">1200 px</output></div>
            <input id="c-maxdim" type="range" min="0" max="2000" step="100" value="1200">
          </div>
        </div>

        <div class="actions">
          <button id="act-download" class="btn primary" type="button">Download SVG</button>
          <div class="actions-row">
            <button id="act-copysvg" class="btn" type="button">Copy SVG</button>
            <button id="act-copypng" class="btn" type="button">Copy PNG</button>
            <button id="act-share" class="btn" type="button" hidden>Share</button>
          </div>
        </div>
      </aside>
    </div>
  </section>
</main>

<footer class="site-footer">
  <div class="footer-inner">
    Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
    · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a>
  </div>
</footer>

<aside id="drawer" class="drawer" aria-hidden="true" aria-label="Event log">
  <div class="drawer-head">
    <div class="drawer-title">Event log</div>
    <button id="drawer-close" class="icon-btn" type="button" aria-label="Close event log">${X_ICON}</button>
  </div>
  <div id="drawer-trail" class="drawer-trail"></div>
</aside>

<input id="file-input" type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/bmp,image/avif,image/*" hidden />
<div id="modal-host" hidden></div>
<div id="toast" class="toast" role="status" aria-live="polite"></div>
`;

const HOW_HTML = /* html */ `
<p>Vectorwell turns a pixel image (${g('raster')}) into a clean, scalable ${g('svg')} — entirely inside your browser tab. Here is what happens when you drop a file in:</p>
<ol class="steps">
  <li><b>Decode & scale.</b> Your image is decoded and, if it is very large, scaled down to the chosen ${g('trace resolution')} so the result stays tidy and fast.</li>
  <li><b>Posterize.</b> In colour mode the picture is reduced to a small set of flat colours (you choose how many). In black &amp; white mode it becomes a single ink layer.</li>
  <li><b>Trace.</b> The ${g('potrace')} engine — the open-source tracer, compiled to ${g('wasm')} — follows every colour boundary and fits smooth Bézier curves to it.</li>
  <li><b>Tune, live.</b> Every slider re-traces instantly. Raise ${g('speckle')} removal to drop noise, lower the corner threshold for sharper corners, or ${g('posterize')} to more colours for detail.</li>
  <li><b>Export.</b> Download the ${g('svg')}, copy its markup straight into a design tool, or share it. Each ${g('path')} is real, editable vector data.</li>
</ol>
<p class="muted">All of this runs on your device. There is no server, no upload and no account.</p>
`;

const PRIVACY_HTML = /* html */ `
<div class="tm">
  <div class="tm-block ok">
    <h3>Protected</h3>
    <ul>
      <li>Your image is decoded and traced entirely on your device. It is <b>never uploaded</b>.</li>
      <li>The resulting SVG is generated locally and only leaves your device if <i>you</i> download, copy or share it.</li>
      <li>The tracer (${g('potrace')} as ${g('wasm')}) is served from this origin and runs offline once cached.</li>
      <li>No cookies, no fingerprinting, no third-party fonts, no image ever sent anywhere.</li>
    </ul>
  </div>
  <div class="tm-block warn">
    <h3>Not protected</h3>
    <ul>
      <li>The initial page load and static assets are served by GitHub Pages, whose CDN sees the request for the <i>page</i> — never your image.</li>
      <li>Anything you deliberately export (download / copy / share) is then in your hands and out of Vectorwell's control.</li>
    </ul>
  </div>
  <div class="tm-block trust">
    <h3>Trust surface</h3>
    <ul>
      <li>The static site bundle (hash-pinned by the GitHub Pages deploy) and the same-origin potrace WebAssembly.</li>
      <li>The TLS connection between you and GitHub Pages.</li>
      <li>A Cloudflare Web Analytics beacon records anonymous page views — no cookies, no fingerprinting, no cross-site tracking; your images and SVGs are never sent to it.</li>
      <li>Feedback you choose to send is sent to feedback.benrichardson.dev. Nothing is sent unless you open the feedback form and press Send; your files and data never are.</li>
    </ul>
  </div>
</div>
`;

const ABOUT_HTML = /* html */ `
<p><b>Vectorwell</b> is a free, private image-to-vector converter. Drop in a logo, icon, sketch or photo and get an editable ${g('svg')} back — no uploads, no accounts, works offline.</p>
<p>It exists because the good online vectorizers either charge, require a login, or send your artwork to a server — a non-starter for an unreleased logo or a client's confidential mark.</p>
<p>Made by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">Ben Richardson</a>. Part of a small lab of privacy-first browser tools — see them all at <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">lab.benrichardson.dev</a>.</p>
<p class="muted">Tracing by <a href="https://github.com/tomayac/esm-potrace-wasm" target="_blank" rel="noopener">esm-potrace-wasm</a>, a WebAssembly build of Peter Selinger's Potrace. Source: <a href="https://github.com/ben-gy/vectorwell" target="_blank" rel="noopener">github.com/ben-gy/vectorwell</a>.</p>
<p class="muted">Shortcuts: <kbd>Esc</kbd> closes dialogs · <kbd>Ctrl/⌘</kbd>+<kbd>V</kbd> pastes an image · drag a file anywhere onto the page.</p>
`;
